import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AnimatePresence } from "framer-motion";
import { useEffect, lazy, Suspense, Component } from "react";

// Context
import { AuthProvider } from "./context/AuthContext";
import { CreditsProvider } from "./context/CreditsContext";
import { UnlockNotificationProvider } from "./context/UnlockNotificationContext";

// Native mobile utilities
import { initializeNative } from "./utils/native";

// Layout
import Navbar from "./components/layout/Navbar";
import ProtectedRoute from "./components/common/ProtectedRoute";
import ScrollToTop from "./components/common/ScrollToTop";

// Critical pages - load immediately
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import SharedStoryPage from "./pages/SharedStoryPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";

// Error Boundary to catch rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-amber-50 p-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">📚</div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Oops! Something went wrong</h1>
            <p className="text-slate-600 mb-6">Please try refreshing the page or come back later.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-500"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-amber-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Lazy-loaded pages - Heavy pages that don't need immediate loading
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateStoryPage = lazy(() => import("./pages/CreateStoryPage"));
const StoryReaderPage = lazy(() => import("./pages/StoryReaderPage"));
const CharactersPage = lazy(() => import("./pages/CharactersPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const TemplateDetailPage = lazy(() => import("./pages/TemplateDetailPage"));
const StoryBuilderPage = lazy(() => import("./pages/StoryBuilderPage"));
const MyStoriesPage = lazy(() => import("./pages/MyStoriesPage"));
const AdminTemplatesPage = lazy(() => import("./pages/AdminTemplatesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const DownloadedTemplatesPage = lazy(() => import("./pages/DownloadedTemplatesPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const MasterAdminPage = lazy(() => import("./pages/MasterAdminPage"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const ParentalControlsPage = lazy(() => import("./pages/ParentalControlsPage"));
const SalesLandingPage = lazy(() => import("./pages/SalesLandingPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const PrintOrderPage = lazy(() => import("./pages/PrintOrderPage"));
const InteractiveStoryWizard = lazy(() => import("./pages/InteractiveStoryWizard"));
const InteractiveReader = lazy(() => import("./pages/InteractiveReader"));
const UnlockablesPage = lazy(() => import("./pages/UnlockablesPage"));
const AdminSandboxPage = lazy(() => import("./pages/AdminSandboxPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const AffiliateResourcesPage = lazy(() => import("./pages/AffiliateResourcesPage"));
const AffiliateApplyPage = lazy(() => import("./pages/AffiliateApplyPage"));

// Guest Pages - lazy loaded
const GuestCharacterPage = lazy(() => import("./pages/GuestCharacterPage"));
const GuestStoryBuilderPage = lazy(() => import("./pages/GuestStoryBuilderPage"));
const GuestStoryViewerPage = lazy(() => import("./pages/GuestStoryViewerPage"));

// Blog Pages - lazy loaded
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AdminBlogPage = lazy(() => import("./pages/AdminBlogPage"));

// Book Library Pages - lazy loaded
const DiscoverBooksPage = lazy(() => import("./pages/DiscoverBooksPage"));
const BookReaderPage = lazy(() => import("./pages/BookReaderPage"));

// Marketing Pages - lazy loaded
const HowItWorksPage = lazy(() => import("./pages/marketing/HowItWorksPage"));
const AIIllustrationsPage = lazy(() => import("./pages/marketing/AIIllustrationsPage"));
const CustomCharactersPage = lazy(() => import("./pages/marketing/CustomCharactersPage"));
const ArtStylesPage = lazy(() => import("./pages/marketing/ArtStylesPage"));
const ReadingExperiencePage = lazy(() => import("./pages/marketing/ReadingExperiencePage"));
const ForParentsPage = lazy(() => import("./pages/marketing/ForParentsPage"));
const ForEducatorsPage = lazy(() => import("./pages/marketing/ForEducatorsPage"));
const StoryStartersPage = lazy(() => import("./pages/marketing/StoryStartersPage"));
const FAQPage = lazy(() => import("./pages/marketing/FAQPage"));
const AboutPage = lazy(() => import("./pages/marketing/AboutPage"));
const PricingPage = lazy(() => import("./pages/marketing/PricingPage"));
const TestimonialsPage = lazy(() => import("./pages/marketing/TestimonialsPage"));
const ContactPage = lazy(() => import("./pages/marketing/ContactPage"));
const TutorialsPage = lazy(() => import("./pages/marketing/TutorialsPage"));
const CharactersTutorialPage = lazy(() => import("./pages/marketing/CharactersTutorialPage"));
const StoriesTutorialPage = lazy(() => import("./pages/marketing/StoriesTutorialPage"));
const AdvancedTutorialPage = lazy(() => import("./pages/marketing/AdvancedTutorialPage"));
const ChooseAdventurePage = lazy(() => import("./pages/marketing/ChooseAdventurePage"));
const PrintStorybooksPage = lazy(() => import("./pages/marketing/PrintStorybooksPage"));
const TutorialGettingStarted = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialGettingStarted })));
const TutorialQuickStory = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialQuickStory })));
const TutorialCharacters = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialCharacters })));
const TutorialCYOA = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialCYOA })));
const TutorialAITools = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialAITools })));
const TutorialLibrary = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialLibrary })));
const TutorialMarketplace = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialMarketplace })));
const TutorialSubscription = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialSubscription })));
const TutorialAchievements = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialAchievements })));
const TutorialImproveStories = lazy(() => import("./pages/marketing/tutorials").then(module => ({ default: module.TutorialImproveStories })));

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      <Routes>
        {/* Public Landing & Auth - Not lazy loaded for fast initial load */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<LoginPage initialMode="register" />} />
        <Route path="/signup" element={<LoginPage initialMode="register" />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/shared/:token" element={<SharedStoryPage />} />
        
        {/* Lazy loaded public pages */}
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/library" element={<DiscoverBooksPage />} />
        <Route path="/library/book/:bookId" element={<BookReaderPage />} />
        
        {/* Sales Landing Page - No Navigation */}
        <Route path="/ai-storybook-creator" element={<SalesLandingPage />} />
        
        {/* Legal Pages */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        
        {/* Marketing Pages */}
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/ai-illustrations" element={<AIIllustrationsPage />} />
        <Route path="/custom-characters" element={<CustomCharactersPage />} />
        <Route path="/art-styles" element={<ArtStylesPage />} />
        <Route path="/reading-experience" element={<ReadingExperiencePage />} />
        <Route path="/for-parents" element={<ForParentsPage />} />
        <Route path="/for-educators" element={<ForEducatorsPage />} />
        <Route path="/story-starters" element={<StoryStartersPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/choose-adventure" element={<ChooseAdventurePage />} />
        <Route path="/print-storybooks" element={<PrintStorybooksPage />} />
        
        {/* Affiliate Pages */}
        <Route path="/affiliate-resources" element={<AffiliateResourcesPage />} />
        <Route path="/affiliate-apply" element={<AffiliateApplyPage />} />
        
        {/* Blog Pages */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/admin/blog" element={<AdminBlogPage />} />
        
        {/* Tutorials */}
        <Route path="/tutorials" element={<TutorialsPage />} />
        <Route path="/tutorials/getting-started" element={<TutorialGettingStarted />} />
        <Route path="/tutorials/quick-story" element={<TutorialQuickStory />} />
        <Route path="/tutorials/characters" element={<TutorialCharacters />} />
        <Route path="/tutorials/cyoa" element={<TutorialCYOA />} />
        <Route path="/tutorials/ai-tools" element={<TutorialAITools />} />
        <Route path="/tutorials/library" element={<TutorialLibrary />} />
        <Route path="/tutorials/marketplace" element={<TutorialMarketplace />} />
        <Route path="/tutorials/subscription" element={<TutorialSubscription />} />
        <Route path="/tutorials/achievements" element={<TutorialAchievements />} />
        <Route path="/tutorials/improve-stories" element={<TutorialImproveStories />} />
        {/* Legacy tutorial routes */}
        <Route path="/tutorials/stories" element={<StoriesTutorialPage />} />
        <Route path="/tutorials/advanced" element={<AdvancedTutorialPage />} />
        
        {/* Guest Routes - No Login Required */}
        <Route path="/try-character" element={<GuestCharacterPage />} />
        <Route path="/try-story" element={<GuestStoryBuilderPage />} />
        <Route path="/try-story/:storyId" element={<GuestStoryViewerPage />} />
        
        {/* Protected App Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><CreateStoryPage /></ProtectedRoute>} />
        <Route path="/builder" element={<ProtectedRoute><StoryBuilderPage /></ProtectedRoute>} />
        <Route path="/characters" element={<ProtectedRoute><CharactersPage /></ProtectedRoute>} />
        <Route path="/stories" element={<ProtectedRoute><MyStoriesPage /></ProtectedRoute>} />
        <Route path="/story/:id" element={<ProtectedRoute><StoryReaderPage /></ProtectedRoute>} />
        <Route path="/print/:storyId" element={<ProtectedRoute><PrintOrderPage /></ProtectedRoute>} />
        <Route path="/interactive/create" element={<ProtectedRoute><InteractiveStoryWizard /></ProtectedRoute>} />
        <Route path="/interactive/:storyId" element={<ProtectedRoute><InteractiveReader /></ProtectedRoute>} />
        <Route path="/unlockables" element={<ProtectedRoute><UnlockablesPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><DownloadedTemplatesPage /></ProtectedRoute>} />
        <Route path="/marketplace/:templateId" element={<TemplateDetailPage />} />
        <Route path="/admin/templates" element={<ProtectedRoute><AdminTemplatesPage /></ProtectedRoute>} />
        <Route path="/admin/sandbox" element={<ProtectedRoute><AdminSandboxPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><MasterAdminPage /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
        <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
        <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
        <Route path="/parental-controls" element={<ProtectedRoute><ParentalControlsPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

// Email Verification Banner
import EmailVerificationBanner from "./components/common/EmailVerificationBanner";

function App() {
  // Initialize native mobile features when app starts
  useEffect(() => {
    initializeNative();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <CreditsProvider>
            <UnlockNotificationProvider>
              <AppContent />
            </UnlockNotificationProvider>
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// Separate component to use useLocation hook
function AppContent() {
  const location = useLocation();
  
  // Pages that should not show the navbar
  const noNavbarRoutes = ['/ai-storybook-creator', '/privacy', '/privacy-policy', '/terms', '/terms-of-service'];
  const hideNavbar = noNavbarRoutes.includes(location.pathname);
  
  return (
    <div className="App min-h-screen bg-slate-50">
      {!hideNavbar && <Navbar />}
      {!hideNavbar && <EmailVerificationBanner />}
      <AnimatePresence mode="wait">
        <AppRouter />
      </AnimatePresence>
      <Toaster richColors position="top-center" />
    </div>
  );
}

export default App;
